import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Alert, Button, Space, Table, Tooltip, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DateRangeFilter, type DateRangeValue } from '../../../components/DateRangeFilter';
import { DashboardCard } from '../../../components/DashboardCard';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag, type StatusTagTone } from '../../../components/StatusTag';
import { useT } from '../../../locales';
import { downloadBlob } from '../../../lib/download';
import { formatCurrency } from '../../../lib/format';
import { paginationProps, usePaginacion } from '../../../lib/paginacion';
import { buildRelationIndex, type MovementRelation } from '../estado-cuenta-relaciones';
import { InvoiceApplications } from './InvoiceApplications';
import {
  useExportClienteEstadoCuentaSimple,
  useClienteEstadoCuentaSimple,
} from '../api/useClientes';
import type {
  EstadoCuentaSimpleMovement,
  EstadoCuentaSimpleTipo,
  QueryEstadoCuentaSimpleDto,
} from '@aid/shared';
import './estado-cuenta.css';

const { Text } = Typography;

function parseAmount(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ddMmYyyy(iso: string): string {
  const d = dayjs(iso);
  if (!d.isValid()) return iso;
  return d.format('DD/MM/YYYY');
}

/** "10/09/2026 - 30/09/2026" for a billing period; single date if only start. */
function formatPeriodo(inicio?: string | null, fin?: string | null): string | null {
  if (inicio && fin) return `${ddMmYyyy(inicio)} - ${ddMmYyyy(fin)}`;
  if (inicio) return ddMmYyyy(inicio);
  return null;
}

/**
 * "Applied to" / "Paid by" reference line: a muted prefix plus one hoverable
 * span per counterpart movement (first two, then a +N remainder). Each span
 * carries the counterpart movement id and its on-page/off-page state.
 */
function ReferenceLine({
  prefix,
  items,
  relation,
  activeTargetIds,
  onRefEnter,
  onRefLeave,
  offPageLabel,
  mutedStyle,
}: {
  prefix: string;
  items: Array<{ label: string; targetId: string }>;
  relation: MovementRelation | undefined;
  activeTargetIds: Set<string>;
  onRefEnter: (targetId: string) => void;
  onRefLeave: () => void;
  offPageLabel: string;
  mutedStyle: React.CSSProperties;
}) {
  const shown = items.slice(0, 2);
  const extra = items.length - shown.length;
  return (
    <span style={mutedStyle}>
      <span>{prefix}</span>{' '}
      {shown.map(({ label, targetId }, index) => {
        const target = relation?.targets.find((entry) => entry.id === targetId);
        const offPage = !target?.onPage;
        const reference = (
          <span
            className={[
              'estado-cuenta__ref',
              offPage ? 'estado-cuenta__ref--offpage' : null,
              !offPage && activeTargetIds.has(targetId) ? 'estado-cuenta__ref--active' : null,
            ]
              .filter(Boolean)
              .join(' ')}
            data-target-id={targetId}
            onMouseEnter={() => onRefEnter(targetId)}
            onMouseLeave={onRefLeave}
          >
            {label}
          </span>
        );
        return (
          <span key={targetId}>
            {index > 0 ? ', ' : null}
            {offPage ? <Tooltip title={offPageLabel}>{reference}</Tooltip> : reference}
          </span>
        );
      })}
      {extra > 0 ? ` +${extra}` : null}
    </span>
  );
}

/** Movement-id prefix of a paying source: payments are `pago:` rows (prepayments
 * live under `prepago:`), an exception-funded link settles the invoice from the
 * `excepcion:` row, and an unknown credit origin has its own `credito:` movement. */
function pagoSourceKind(source?: string): 'pago' | 'excepcion' | 'credito' {
  if (source === 'EXCEPCION') return 'excepcion';
  if (source === 'CREDITO') return 'credito';
  return 'pago';
}

/** Renders a signed net balance as "Deuda Qx" / "A favor Qx" / "Q 0.00". */
function NetBalance({ value, t }: { value: string; t: ReturnType<typeof useT> }) {
  const n = parseAmount(value);
  if (n > 0)
    return (
      <Text
        style={{ color: 'var(--ant-color-success)', fontWeight: 600 }}
      >{`${t('pages.clientes.estadoCuentaSimple.aFavor')} ${formatCurrency(value)}`}</Text>
    );
  if (n < 0)
    return (
      <Text
        style={{ color: 'var(--ant-color-error)', fontWeight: 600 }}
      >{`${t('pages.clientes.estadoCuentaSimple.deuda')} ${formatCurrency(parseAmount(value).toFixed(2).replace('-', ''))}`}</Text>
    );
  return <Text type="secondary">{t('pages.clientes.estadoCuentaSimple.ceros')}</Text>;
}

const TIPO_TONE: Record<EstadoCuentaSimpleTipo, StatusTagTone> = {
  FACTURA: 'neutral',
  PAGO: 'info',
  EXCEPCION: 'warning',
};

export function ClienteEstadoCuentaSimple({ clienteId }: { clienteId: number }) {
  const t = useT();
  const { token } = theme.useToken();
  const { page, pageSize, handleChange, setPage } = usePaginacion(20);
  const [range, setRange] = useState<DateRangeValue | null>(null);
  const exportMutation = useExportClienteEstadoCuentaSimple(clienteId);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  // Cross-reference interaction: a transient hover preview and a pinned row.
  const [hoverRow, setHoverRow] = useState<string | null>(null);
  const [hoverRef, setHoverRef] = useState<string | null>(null);
  const [pinnedRow, setPinnedRow] = useState<string | null>(null);

  const queryParams: QueryEstadoCuentaSimpleDto = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    fechaDesde: range?.[0] ?? undefined,
    fechaHasta: range?.[1] ?? undefined,
  };
  const query = useClienteEstadoCuentaSimple(clienteId, queryParams);
  const resumen = query.data?.resumen;
  const rows = query.data?.data ?? [];

  /** Symmetric relation index for the current page (movements without relations are absent). */
  const relationIndex = useMemo(() => buildRelationIndex(rows), [rows]);

  // Escape clears the pin; a page navigation drops all relation state.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPinnedRow(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setHoverRow(null);
    setHoverRef(null);
    setPinnedRow(null);
  }, [query.data]);

  const updateRange = (value: DateRangeValue | null) => {
    setRange(value);
    setPage(1);
  };

  /** Active relation preview: the hover wins, the pin shows again on mouse-out. */
  const activeRow = hoverRow ?? pinnedRow;
  const activeTargets = activeRow ? relationIndex.get(activeRow) : undefined;
  const visibleTargets = hoverRef
    ? (activeTargets?.targets ?? []).filter((target) => target.id === hoverRef)
    : (activeTargets?.targets ?? []);
  const activeTargetIds = new Set(visibleTargets.map((target) => target.id));

  const togglePin = (rowId: string) => {
    setPinnedRow((current) => (current === rowId ? null : rowId));
  };

  const rowHandlers = (
    m: EstadoCuentaSimpleMovement,
    relation: MovementRelation | undefined,
  ): React.HTMLAttributes<HTMLElement> => ({
    className:
      [
        relation ? 'estado-cuenta__row--linked' : null,
        activeRow === m.id ? 'estado-cuenta__row--source' : null,
        activeTargetIds.has(m.id) ? 'estado-cuenta__row--related' : null,
      ]
        .filter(Boolean)
        .join(' ') || undefined,
    ...(!relation
      ? {}
      : {
          tabIndex: 0,
          onClick: () => togglePin(m.id),

          onMouseEnter: () => setHoverRow(m.id),
          onMouseLeave: () => {
            setHoverRow(null);
            setHoverRef(null);
          },
          onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              togglePin(m.id);
            }
          },
        }),
  });

  const handleExport = async (format: 'pdf' | 'xlsx') => {
    setExporting(format);
    exportMutation.reset();
    try {
      // Export the simple statement (what the user sees), filtered by the date range.
      const blob = await exportMutation.mutateAsync({
        format,
        filters: {
          fechaDesde: range?.[0] ?? undefined,
          fechaHasta: range?.[1] ?? undefined,
        },
      });
      downloadBlob(blob, `estado-cuenta-${clienteId}.${format}`);
    } catch {
      // keep the alert for recovery
    } finally {
      setExporting(null);
    }
  };

  const periodoLabel =
    resumen && (resumen.desde || resumen.hasta)
      ? t('pages.clientes.estadoCuentaSimple.periodoDel', {
          desde: dayjs(resumen.desde ?? '').format('DD/MM/YYYY'),
          hasta: dayjs(resumen.hasta ?? '').format('DD/MM/YYYY'),
        })
      : t('pages.clientes.estadoCuentaSimple.periodoCompleto');

  const columns: ColumnsType<EstadoCuentaSimpleMovement> = [
    {
      title: t('pages.clientes.estadoCuentaSimple.fecha'),
      key: 'fecha',
      width: 110,
      render: (_v, m) => (
        <Space orientation="vertical" size={0}>
          <Text>{dayjs(m.fecha).format('DD/MM/YYYY')}</Text>
          {m.fechaRegistro && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('pages.clientes.estadoCuentaSimple.fechaRegistro', {
                fecha: dayjs(m.fechaRegistro).format('DD/MM/YYYY'),
              })}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.tipo'),
      key: 'tipo',
      width: 120,
      render: (_v, m) => (
        <StatusTag tone={TIPO_TONE[m.tipo] ?? 'neutral'}>
          {t(`pages.clientes.estadoCuentaSimple.tipo.${m.tipo}`)}
        </StatusTag>
      ),
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.referencia'),
      key: 'referencia',
      render: (_v, m) => {
        const periodo = m.tipo === 'FACTURA' ? formatPeriodo(m.periodoInicio, m.periodoFin) : null;
        const relation = relationIndex.get(m.id);
        return (
          <Space orientation="vertical" size={0} style={{ minWidth: 0 }}>
            <Text strong>{m.referencia}</Text>
            {periodo && (
              <Text style={{ fontSize: 11, color: token.colorTextSecondary }}>{periodo}</Text>
            )}
            {m.concepto && m.concepto !== m.referencia.split(' ')[0] && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {m.concepto}
              </Text>
            )}
            {m.aplicadoA?.length ? (
              <ReferenceLine
                prefix={t('pages.clientes.estadoCuentaSimple.aplicadoAPrefix')}
                items={m.aplicadoA.map((item, index) => ({
                  label: index === 0 ? `Factura #${item.idFactura}` : `#${item.idFactura}`,
                  targetId: `factura:${item.idFactura}`,
                }))}
                relation={relation}
                activeTargetIds={activeTargetIds}
                onRefEnter={setHoverRef}
                onRefLeave={() => setHoverRef(null)}
                offPageLabel={t('pages.clientes.estadoCuentaSimple.enOtraPagina')}
                mutedStyle={{ fontSize: 11, color: token.colorTextSecondary }}
              />
            ) : null}
            {m.pagadoPor?.length ? (
              <ReferenceLine
                prefix={t('pages.clientes.estadoCuentaSimple.pagadoPorPrefix')}
                items={m.pagadoPor.map((item, index) => {
                  const kind = pagoSourceKind(item.source);
                  // An exception-funded link references the EXCEPTION, not a payment,
                  // so the label follows the source instead of assuming `idPago`.
                  const ref =
                    kind === 'credito' ? item.idCredito : (item.idReferencia ?? item.idPago);
                  return {
                    label:
                      index === 0
                        ? `${t('pages.clientes.estadoCuentaSimple.pagadoPorSource.' + kind)} #${ref}`
                        : `#${ref}`,
                    targetId: `${kind}:${ref}`,
                  };
                })}
                relation={relation}
                activeTargetIds={activeTargetIds}
                onRefEnter={setHoverRef}
                onRefLeave={() => setHoverRef(null)}
                offPageLabel={t('pages.clientes.estadoCuentaSimple.enOtraPagina')}
                mutedStyle={{ fontSize: 11, color: token.colorTextSecondary }}
              />
            ) : null}
            {m.tipo === 'FACTURA' ? <InvoiceApplications applications={m.aplicaciones} /> : null}
            {m.tipo === 'PAGO' && m.creditPark != null && parseAmount(m.creditPark) > 0 ? (
              <Text style={{ fontSize: 11, color: token.colorTextSecondary }}>
                {t('pages.clientes.estadoCuentaSimple.coberturaCredito', {
                  monto: formatCurrency(m.creditPark),
                })}
              </Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.debito'),
      key: 'debito',
      width: 110,
      align: 'right',
      render: (_v, m) =>
        parseAmount(m.debito) > 0 ? (
          <Text style={{ color: token.colorError, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(m.debito)}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.credito'),
      key: 'credito',
      width: 110,
      align: 'right',
      render: (_v, m) =>
        parseAmount(m.credito) > 0 ? (
          <Text style={{ color: token.colorSuccess, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(m.credito)}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.estadoTitulo'),
      key: 'estado',
      width: 110,
      render: (_v, m) => {
        // Estado is type-dependent: for a payment, VIGENTE means the payment is
        // valid (not "pending"). Resolve the label key by movement type.
        const keyPrefix = m.tipo === 'PAGO' ? 'pago' : 'estado';
        const translated = t(`pages.clientes.estadoCuentaSimple.${keyPrefix}.${m.estado}`);
        const label =
          typeof translated === 'string' && translated.startsWith('pages.') ? m.estado : translated;
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        );
      },
    },
    {
      title: t('pages.clientes.estadoCuentaSimple.saldo'),
      key: 'saldo',
      width: 130,
      align: 'right',
      render: (_v, m) => <NetBalance value={m.saldo} t={t} />,
    },
  ];

  return (
    <div className="estado-cuenta">
      <DashboardCard
        title={t('pages.clientes.estadoCuentaSimple.title')}
        subtitle={t('pages.clientes.estadoCuentaSimple.subtitle')}
        headerClassName="estado-cuenta__header"
        extra={
          <Space size={8} className="estado-cuenta__export-actions">
            <Button
              size="small"
              icon={<FilePdfOutlined aria-hidden="true" />}
              loading={exporting === 'pdf'}
              onClick={() => void handleExport('pdf')}
            >
              {t('pages.clientes.estadoCuenta.exportPdf')}
            </Button>
            <Button
              size="small"
              icon={<FileExcelOutlined aria-hidden="true" />}
              loading={exporting === 'xlsx'}
              onClick={() => void handleExport('xlsx')}
            >
              {t('pages.clientes.estadoCuenta.exportExcel')}
            </Button>
          </Space>
        }
        bodyStyle={{ padding: '18px 22px 22px' }}
      >
        {query.isError ? (
          <EmptyState
            compact
            title={t('pages.clientes.estadoCuentaSimple.errorTitle')}
            description={t('pages.clientes.estadoCuentaSimple.errorDescription')}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
          />
        ) : (
          <>
            {exportMutation.error && (
              <Alert
                type="error"
                showIcon
                closable
                style={{ marginBottom: 16 }}
                title={t('pages.clientes.estadoCuenta.exportError')}
                onClose={() => exportMutation.reset()}
              />
            )}

            {/* Period + summary */}
            <div className="estado-cuenta__toolbar" style={{ marginBottom: 12 }}>
              <Space wrap size={[8, 8]}>
                <DateRangeFilter value={range} onChange={updateRange} />
                <Text type="secondary">{periodoLabel}</Text>
              </Space>
            </div>

            {resumen && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <SummaryCell
                  label={t('pages.clientes.estadoCuentaSimple.saldoInicial')}
                  saldo={resumen.saldoInicial}
                  t={t}
                />
                <SummaryCell
                  label={t('pages.clientes.estadoCuentaSimple.facturado')}
                  value={`+ ${formatCurrency(resumen.facturado)}`}
                  tone="debt"
                />
                <SummaryCell
                  label={t('pages.clientes.estadoCuentaSimple.pagado')}
                  value={`− ${formatCurrency(resumen.pagado)}`}
                  tone="credit"
                />
                <SummaryCell
                  label={t('pages.clientes.estadoCuentaSimple.excepciones')}
                  value={`${parseAmount(resumen.excepciones) < 0 ? '−' : '+'} ${formatCurrency(Math.abs(parseAmount(resumen.excepciones)).toFixed(2))}`}
                  tone={parseAmount(resumen.excepciones) < 0 ? 'debt' : 'credit'}
                />
                <SummaryCell
                  label={t('pages.clientes.estadoCuentaSimple.saldoFinal')}
                  saldo={resumen.saldoFinal}
                  t={t}
                  strong
                />
              </div>
            )}

            <Table<EstadoCuentaSimpleMovement>
              size="small"
              rowKey="id"
              dataSource={rows}
              columns={columns}
              loading={query.isLoading}
              pagination={paginationProps(t, {
                page,
                pageSize,
                total: query.data?.total ?? 0,
              })}
              onChange={handleChange}
              onRow={(m) => rowHandlers(m, relationIndex.get(m.id))}
              scroll={{ x: 700 }}
              locale={{
                emptyText: (
                  <EmptyState
                    compact
                    title={t('pages.clientes.estadoCuentaSimple.emptyTitle')}
                    description={t('pages.clientes.estadoCuentaSimple.emptyDescription')}
                  />
                ),
              }}
            />
          </>
        )}
      </DashboardCard>
    </div>
  );
}

function SummaryCell({
  label,
  value,
  saldo,
  tone,
  t,
  strong,
}: {
  label: string;
  value?: string;
  saldo?: string;
  tone?: 'debt' | 'credit';
  t?: ReturnType<typeof useT>;
  strong?: boolean;
}) {
  const color =
    tone === 'debt'
      ? 'var(--ant-color-error)'
      : tone === 'credit'
        ? 'var(--ant-color-success)'
        : undefined;
  return (
    <div
      style={{
        border: '1px solid var(--ant-color-border-secondary)',
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
        {label}
      </Text>
      {saldo !== undefined && t ? (
        <NetBalance value={saldo} t={t} />
      ) : (
        <Text strong={strong} style={{ color, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Text>
      )}
    </div>
  );
}
